// src/services/docusealService.js

import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase/firebase';

class DocuSealService {
  constructor() {
    this.getStatus = httpsCallable(functions, 'getDocuSealStatus');
    this.deleteSubmission = httpsCallable(functions, 'deleteDocuSealSubmission');
    this.createTemplateFromPdfFunction = httpsCallable(functions, 'createDocuSealTemplateFromPdf');
    this.createSubmissionWithTemplateFunction = httpsCallable(functions, 'createDocuSealSubmissionHttp');
    this.checkTemplateStatusFunction = httpsCallable(functions, 'checkTemplateStatus');
    this.getMobileSigningUrlFunction = httpsCallable(functions, 'getMobileSigningUrl');
    this.getMobileDocumentSigningUrlsFunction = httpsCallable(functions, 'getMobileDocumentSigningUrls');
    this.testSubmittersDataStorageFunction = httpsCallable(functions, 'testSubmittersDataStorage');
  }

  // Get submission status from DocuSeal
  async getSubmissionStatus(submissionId) {
    try {
      const result = await this.getStatus({ submissionId });
      return result.data;
    } catch (error) {
      console.error('Error getting DocuSeal status:', error);
      throw error;
    }
  }

  // Update document status in Firestore
  async updateDocumentStatus(projectId, documentId, status, additionalData = {}) {
    try {
      const docRef = doc(db, `projects/${projectId}/documents/${documentId}`);
      await updateDoc(docRef, {
        status,
        updatedAt: new Date(),
        ...additionalData
      });
    } catch (error) {
      console.error('Error updating document status:', error);
      throw error;
    }
  }

  // Get document details
  async getDocument(projectId, documentId) {
    try {
      console.log('🔍 getDocument called with:', { projectId, documentId });
      const docRef = doc(db, `projects/${projectId}/documents/${documentId}`);
      console.log('🔍 Document path:', `projects/${projectId}/documents/${documentId}`);
      
      const docSnap = await getDoc(docRef);
      console.log('🔍 Document snapshot exists:', docSnap.exists());
      
      if (docSnap.exists()) {
        const documentData = { id: docSnap.id, ...docSnap.data() };
        console.log('🔍 Document data:', documentData);
        return documentData;
      } else {
        console.error('❌ Document not found at path:', `projects/${projectId}/documents/${documentId}`);
        throw new Error('Document not found');
      }
    } catch (error) {
      console.error('❌ Error getting document:', error);
      throw error;
    }
  }

  // Check if document is ready for signing
  async checkDocumentReady(projectId, documentId) {
    try {
      const document = await this.getDocument(projectId, documentId);
      
      return {
        ready: document.status === 'draft' && document.selectedTeams?.length > 0,
        document,
        message: this.getStatusMessage(document.status)
      };
    } catch (error) {
      console.error('Error checking document readiness:', error);
      throw error;
    }
  }

  // Get status message for UI
  getStatusMessage(status) {
    switch (status) {
      case 'draft':
        return 'Document is ready to be sent for signing';
      case 'sent':
        return 'Document has been sent to recipients';
      case 'signed':
        return 'Document is being signed';
      case 'completed':
        return 'Document has been signed by all parties';
      case 'declined':
        return 'Document was declined by a recipient';
      default:
        return 'Unknown status';
    }
  }

  // Get signing URL for embedded signing
  async getSigningUrl(submissionId, recipientEmail) {
    try {
      // Call Firebase function to get signing URL from DocuSeal API
      const result = await this.getStatus({
        submissionId: submissionId,
        recipientEmail: recipientEmail
      });
      
      if (result.data && result.data.signingUrl) {
        return result.data.signingUrl;
      } else {
        // Fallback to constructing URL if API doesn't return it
        return `https://docuseal.eu/s/${submissionId}`;
      }
    } catch (error) {
      // Fallback to constructing URL
      return `https://docuseal.eu/s/${submissionId}`;
    }
  }

  // Create template from PDF with fields
  async createTemplateFromPdf(projectId, documentId, fields) {
    try {
      console.log('🔍 Starting createTemplateFromPdf with:', { projectId, documentId, fieldsCount: fields.length });
      
      const document = await this.getDocument(projectId, documentId);
      
      if (!document) {
        throw new Error('Document not found');
      }

      console.log('📄 Document found:', document);

      // Create template with fields using the new Firebase function
      console.log('🔧 Calling createDocuSealTemplateFromPdf...');
      
      const templateResult = await this.createTemplateFromPdfFunction({
        projectId,
        documentId,
        document: document,
        fields: fields
      });

      console.log('✅ Template created with fields:', templateResult.data);
      
      return {
        success: true,
        templateId: templateResult.data.templateId,
        message: 'Template created with fields successfully',
        fieldsCount: templateResult.data.fieldsCount
      };
    } catch (error) {
      console.error('❌ Error in createTemplateFromPdf:', error);
      throw error;
    }
  }

  // Check template status
  async checkTemplateStatus(templateId) {
    try {
      console.log('🔍 Checking template status:', { templateId });
      
      const result = await this.checkTemplateStatusFunction({ templateId });
      
      console.log('✅ Template status result:', result.data);
      
      return result.data;
    } catch (error) {
      console.error('❌ Error checking template status:', error);
      throw error;
    }
  }

  // Create submission with template and recipients
  async createSubmissionWithTemplate(projectId, documentId, templateId, recipients, fields = []) {
    try {
      console.log('🔍 Starting createSubmissionWithTemplate with:', { 
        projectId, 
        documentId, 
        templateId, 
        recipientsCount: recipients.length,
        fieldsCount: fields.length
      });

      // DocuSeal assigns roles to submitters by array POSITION, not by role string.
      // We must send submitters in the same order as roles appear in the template.
      const roleOrder = [];
      const seenRoles = new Set();
      for (const field of fields) {
        if (field.role && !seenRoles.has(field.role)) {
          seenRoles.add(field.role);
          roleOrder.push(field.role);
        }
      }

      const recipientByName = new Map(recipients.map(r => [r.name, r]));
      const orderedRecipients = roleOrder
        .map(role => recipientByName.get(role))
        .filter(Boolean);

      if (orderedRecipients.length !== roleOrder.length) {
        const missingRoles = roleOrder.filter(r => !recipientByName.has(r));
        console.warn('⚠️ Some template roles have no matching recipient:', missingRoles);
      }
      if (orderedRecipients.length === 0) {
        throw new Error('No recipients match the template roles. Check that recipient names match field roles.');
      }

      // Build clean recipients in template role order so DocuSeal assigns correctly
      const cleanRecipients = orderedRecipients.map((recipient) => {
        // Create unique name to avoid conflicts
        let uniqueName = recipient.name;
        if (recipient.isPM) {
          uniqueName = `${recipient.name} (PM)`;
        } else if (recipient.teamName && recipient.teamName !== 'You') {
          uniqueName = `${recipient.name} (${recipient.teamName})`;
        }
        
        // IMPORTANT: Role must match the field role exactly
        // Fields use recipient.name as role, so recipients must also use recipient.name
        // This ensures DocuSeal correctly matches fields to recipients
        const role = recipient.name;
        
        const cleanRecipient = {
          name: uniqueName,
          role: role  // Use recipient name to match field roles
        };
        
        // Email is required by DocuSeal - always provide one
        if (recipient.email) {
          cleanRecipient.email = recipient.email;
        } else if (recipient.phone) {
          // Phone users should now have emails from signup
          cleanRecipient.phone = recipient.phone;
          // If somehow no email, use a fallback
          cleanRecipient.email = `phone${recipient.phone.replace(/[^0-9]/g, '')}@crewchain.co.uk`;
        } else {
          // Fallback - should not happen but just in case
          cleanRecipient.email = `unknown@docuseal.local`;
        }
        
        return cleanRecipient;
      });

      console.log('📤 Clean recipients for API:', cleanRecipients);

      // Create submission using callable function
      const submissionResult = await this.createSubmissionWithTemplateFunction({
        projectId,
        documentId,
        templateId,
        recipients: cleanRecipients
      });

      console.log('✅ Submission created:', submissionResult.data);
      
      // Store signing slugs in Firestore for mobile access
      if (submissionResult.data.submitters && submissionResult.data.submitters.length > 0) {
        try {
          console.log('📱 Storing signing slugs for mobile access:', submissionResult.data.submitters);
          
          // Update the document with submitters and their signing slugs
          await this.updateDocumentStatus(projectId, documentId, 'sent', {
            docusealSubmitters: submissionResult.data.submitters,
            signingSlugs: submissionResult.data.submitters.reduce((acc, submitter) => {
              acc[submitter.email] = {
                slug: submitter.slug,
                embed_src: submitter.embed_src,
                name: submitter.name,
                role: submitter.role
              };
              return acc;
            }, {})
          });
          
          console.log('✅ Signing slugs stored successfully');
        } catch (slugError) {
          console.warn('⚠️ Warning: Failed to store signing slugs:', slugError);
          // Don't fail the whole operation if storing slugs fails
        }
      }
      
      return {
        success: true,
        submissionId: submissionResult.data.submissionId,
        templateId: templateId,
        message: 'Submission created successfully',
        recipientsCount: recipients.length,
        submitters: submissionResult.data.submitters || []
      };
    } catch (error) {
      console.error('❌ Error in createSubmissionWithTemplate:', error);
      throw error;
    }
  }





  // Get signing progress
  async getSigningProgress(projectId, documentId) {
    try {
      const document = await this.getDocument(projectId, documentId);
      
      if (!document.docusealSubmissionId) {
        return { progress: 0, status: document.status };
      }

      const status = await this.getSubmissionStatus(document.docusealSubmissionId);
      
      return {
        progress: status.progress || 0,
        status: status.status || document.status,
        recipients: status.recipients || document.recipients || [],
        completedAt: status.completedAt
      };
    } catch (error) {
      throw error;
    }
  }

  // Download completed document
  async downloadCompletedDocument(projectId, documentId) {
    try {
      const document = await this.getDocument(projectId, documentId);
      
      if (document.status !== 'completed') {
        throw new Error('Document is not completed');
      }

      if (!document.completedFileURL) {
        throw new Error('Completed document not found');
      }

      // Create download link
      const link = document.createElement('a');
      link.href = document.completedFileURL;
      link.download = `completed_${document.name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return {
        success: true,
        url: document.completedFileURL
      };
    } catch (error) {
      throw error;
    }
  }

  // Cancel DocuSeal submission and archive template
  async cancelSubmission(submissionId, templateId) {
    try {
      // Call Firebase function to delete DocuSeal submission and archive template
      const result = await this.deleteSubmission({
        submissionId: submissionId,
        templateId: templateId
      });
      
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  // Download completed or partial document from DocuSeal
  async downloadCompletedDocumentFromDocuSeal(projectId, documentId, submissionId) {
    try {
      const downloadDocuments = httpsCallable(functions, 'downloadCompletedDocuments');
      const result = await downloadDocuments({
        projectId: projectId,
        documentId: documentId,
        submissionId: submissionId
      });
      
      return result.data;
    } catch (error) {
      throw new Error(`Failed to download document: ${error.message}`);
    }
  }

  // Get signing slugs for mobile access
  async getSigningSlugs(projectId, documentId) {
    try {
      console.log('🔍 Getting signing slugs for mobile:', { projectId, documentId });
      
      const document = await this.getDocument(projectId, documentId);
      
      if (!document.docusealSubmitters) {
        console.log('ℹ️ No submitters found for document');
        return null;
      }
      
      // Return signing slugs organized by email for easy mobile lookup
      const signingSlugs = document.docusealSubmitters.reduce((acc, submitter) => {
        acc[submitter.email] = {
          slug: submitter.slug,
          embed_src: submitter.embed_src,
          name: submitter.name,
          role: submitter.role,
          submission_id: submitter.submission_id
        };
        return acc;
      }, {});
      
      console.log('✅ Signing slugs retrieved:', signingSlugs);
      return signingSlugs;
    } catch (error) {
      console.error('❌ Error getting signing slugs:', error);
      throw error;
    }
  }

  // Get signing URL for a specific recipient
  async getRecipientSigningUrl(projectId, documentId, recipientEmail) {
    try {
      console.log('🔍 Getting signing URL for recipient:', { projectId, documentId, recipientEmail });
      
      const signingSlugs = await this.getSigningSlugs(projectId, documentId);
      
      if (!signingSlugs || !signingSlugs[recipientEmail]) {
        console.log('ℹ️ No signing slug found for recipient:', recipientEmail);
        return null;
      }
      
      const recipientData = signingSlugs[recipientEmail];
      
      // Construct signing URL using slug or embed_src
      let signingUrl;
      if (recipientData.embed_src) {
        signingUrl = recipientData.embed_src;
      } else if (recipientData.slug) {
        signingUrl = `https://docuseal.eu/s/${recipientData.slug}`;
      } else {
        throw new Error('No signing URL available for recipient');
      }
      
      console.log('✅ Signing URL constructed:', signingUrl);
      return {
        signingUrl,
        recipientData
      };
    } catch (error) {
      console.error('❌ Error getting recipient signing URL:', error);
      throw error;
    }
  }

  // Mobile API: Get signing URL for a specific recipient
  async getMobileSigningUrl(projectId, documentId, recipientIdentifier) {
    try {
      console.log('📱 Mobile API: Getting signing URL for:', { projectId, documentId, recipientIdentifier });
      
      const document = await this.getDocument(projectId, documentId);
      
      if (!document.docusealSubmitters) {
        throw new Error('Document has no signing slugs available');
      }
      
      // Find the recipient's signing data by email or phone
      const recipientData = document.docusealSubmitters.find(
        submitter => submitter.email === recipientIdentifier || submitter.phone === recipientIdentifier
      );
      
      if (!recipientData) {
        throw new Error('Recipient not found in document');
      }
      
      // Construct signing URL
      let signingUrl;
      if (recipientData.embed_src) {
        signingUrl = recipientData.embed_src;
      } else if (recipientData.slug) {
        signingUrl = `https://docuseal.eu/s/${recipientData.slug}`;
      } else {
        throw new Error('No signing URL available for recipient');
      }
      
      return {
        success: true,
        signingUrl: signingUrl,
        recipientData: {
          name: recipientData.name,
          email: recipientData.email,
          phone: recipientData.phone,
          role: recipientData.role,
          submission_id: recipientData.submission_id
        },
        documentInfo: {
          name: document.name,
          status: document.status,
          projectId: projectId,
          documentId: documentId
        }
      };
    } catch (error) {
      console.error('❌ Mobile API error:', error);
      throw error;
    }
  }

  // Mobile API: Get all signing URLs for a document
  async getMobileDocumentSigningUrls(projectId, documentId) {
    try {
      console.log('📱 Mobile API: Getting all signing URLs for document:', { projectId, documentId });
      
      const document = await this.getDocument(projectId, documentId);
      
      if (!document.docusealSubmitters) {
        throw new Error('Document has no signing slugs available');
      }
      
      // Map all recipients to their signing URLs
      const signingUrls = document.docusealSubmitters.map(submitter => {
        let signingUrl;
        if (submitter.embed_src) {
          signingUrl = submitter.embed_src;
        } else if (submitter.slug) {
          signingUrl = `https://docuseal.eu/s/${submitter.slug}`;
        } else {
          signingUrl = null;
        }
        
        return {
          name: submitter.name,
          email: submitter.email,
          phone: submitter.phone,
          role: submitter.role,
          submission_id: submitter.submission_id,
          signingUrl: signingUrl,
          hasSigningUrl: !!signingUrl
        };
      });
      
      return {
        success: true,
        documentInfo: {
          name: document.name,
          status: document.status,
          projectId: projectId,
          documentId: documentId
        },
        signingUrls: signingUrls,
        totalRecipients: signingUrls.length,
        availableSigningUrls: signingUrls.filter(s => s.hasSigningUrl).length
      };
    } catch (error) {
      console.error('❌ Mobile API error:', error);
      throw error;
    }
  }

  // Test submitters data storage (debug function)
  async testSubmittersDataStorage(projectId, documentId) {
    try {
      console.log('🧪 Testing submitters data storage for:', { projectId, documentId });
      
      const result = await this.testSubmittersDataStorageFunction({
        projectId,
        documentId
      });
      
      console.log('✅ Test result:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Error testing submitters data storage:', error);
      throw error;
    }
  }

  // Get document templates
  async getTemplates() {
    // This would fetch from DocuSeal API
    // For now, return mock templates
    return [
      {
        id: 'contract-template',
        name: 'Construction Contract',
        description: 'Standard construction contract template',
        category: 'contracts'
      },
      {
        id: 'change-order-template',
        name: 'Change Order Form',
        description: 'Change order request template',
        category: 'forms'
      },
      {
        id: 'payment-template',
        name: 'Payment Application',
        description: 'Payment application template',
        category: 'forms'
      }
    ];
  }
}

const docusealService = new DocuSealService();
export default docusealService; 